import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useSettings } from "@/lib/SettingsContext";
import { useShowEvent } from "@/lib/useShowEvent";

const navLinks = [
  { to: "/", label: "Home", id: "nav-home" },
  { to: "/treatments", label: "Treatments", id: "nav-treatments" },
  { to: "/gallery", label: "Gallery", id: "nav-gallery" },
  { to: "/about", label: "About", id: "nav-about" },
  { to: "/reviews", label: "Reviews", id: "nav-reviews" },
  { to: "/contact", label: "Contact", id: "nav-contact" },
];

export const Navbar = () => {
  const { brand: BRAND, images: IMAGES } = useSettings();
  const showEvent = useShowEvent();
  const location = useLocation();
  const bannerVisible = showEvent && !location.pathname.startsWith("/contact");
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // When the announcement banner is visible above the navbar, push the
  // navbar down by the banner's approximate height so both are readable.
  const topOffset = bannerVisible && !scrolled ? "top-[44px] sm:top-[40px]" : "top-0";

  return (
    <header
      data-testid="site-navbar"
      className={`fixed ${topOffset} inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "glass border-b border-blush-deep/40 py-2 md:py-3" : "py-3 md:py-5 bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 flex items-center justify-between">
        <Link
          to="/"
          data-testid="brand-logo-link"
          className="flex items-center gap-3 leading-none"
          onClick={() => setOpen(false)}
        >
          {IMAGES?.logo ? (
            <img
              src={IMAGES.logo}
              alt={BRAND.name}
              data-testid="brand-logo-image"
              className="h-12 sm:h-16 md:h-24 lg:h-28 w-auto object-contain"
            />
          ) : (
            <span className="flex flex-col leading-none">
              <span className="font-display italic text-3xl text-plum">{BRAND.name}</span>
              <span className="text-[10px] tracking-[0.32em] uppercase text-plum-light mt-0.5">
                {BRAND.tagline}
              </span>
            </span>
          )}
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {navLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.id}
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors ${
                  isActive ? "text-plum font-semibold" : "text-foreground/70 hover:text-plum"
                }`
              }
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <Link
          to="/contact"
          data-testid="nav-book-now-btn"
          className="hidden md:inline-flex items-center rounded-full bg-plum text-white px-5 py-2.5 text-sm font-medium hover:bg-plum-light transition-all hover:-translate-y-0.5 shadow-sm"
        >
          Book Now
        </Link>

        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden p-2 text-plum"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu-panel" className="md:hidden glass border-t border-blush-deep/40">
          <div className="px-6 py-6 flex flex-col gap-4">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-${l.id}`}
                className={({ isActive }) =>
                  `py-2 text-base ${isActive ? "text-plum font-semibold" : "text-foreground/80"}`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              data-testid="mobile-nav-book-now-btn"
              className="mt-2 inline-flex justify-center rounded-full bg-plum text-white px-5 py-3 text-sm font-medium"
            >
              Book Now
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
